import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseSlots(value) {
  return String(value || '').split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [day, hour] = part.split(':').map(Number);
      return { day_of_week: day, hour };
    })
    .filter((slot) => slot.day_of_week >= 0 && slot.day_of_week <= 6
      && slot.hour >= 0 && slot.hour <= 23);
}

function slotSummary(slots) {
  if (!Array.isArray(slots) || !slots.length) return 'No selected hours';
  return slots.map((slot) => `${days[slot.day_of_week] || slot.day_of_week} ${String(slot.hour).padStart(2, '0')}:00`).join(', ');
}

export default function FranchiseSyndicationSettings() {
  const [stations, setStations] = useState([]);
  const [status, setStatus] = useState({ links: [] });
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [message, setMessage] = useState(null);
  const [invite, setInvite] = useState(null);
  const [policyDrafts, setPolicyDrafts] = useState({});
  const [inviteForm, setInviteForm] = useState({
    sourceStationId: '',
    overrideMode: 'none',
    overrideSlots: '',
    localBreakDefault: 'parent',
    logHorizonHours: 168,
    advertisingEnabled: true,
  });
  const [acceptForm, setAcceptForm] = useState({ inviteCode: '', destinationStationId: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statusResponse, stationResponse] = await Promise.all([
        axios.get(`${API_URL}/api/syndication/status`, { withCredentials: true }),
        axios.get(`${API_URL}/api/stations`, { withCredentials: true }),
      ]);
      setStatus(statusResponse.data || { links: [] });
      setStations(stationResponse.data || []);
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setPolicyDrafts(Object.fromEntries((status.links || []).map((link) => [
      link.link_id,
      {
        mode: link.override_mode || 'none',
        slots: (link.override_slots || [])
          .map((slot) => `${slot.day_of_week}:${slot.hour}`)
          .join(','),
      },
    ])));
  }, [status.links]);

  const parentLinks = useMemo(() => (status.links || []).filter((link) => link.role === 'parent'), [status.links]);
  const childLinks = useMemo(() => (status.links || []).filter((link) => link.role === 'child'), [status.links]);

  const createInvite = async () => {
    const requestedSlots = String(inviteForm.overrideSlots || '').split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    const parsedSlots = parseSlots(inviteForm.overrideSlots);
    const overrideMode = Number(status.rolloutStage) >= 3
      ? inviteForm.overrideMode
      : 'none';
    if (overrideMode === 'selected'
        && (!requestedSlots.length || parsedSlots.length !== requestedSlots.length)) {
      setMessage({
        severity: 'error',
        text: 'Selected override hours must all use day:hour format (day 0-6, hour 0-23).',
      });
      return;
    }
    setWorking('invite');
    setMessage(null);
    try {
      const response = await axios.post(`${API_URL}/api/syndication/invite`, {
        sourceStationId: inviteForm.sourceStationId,
        policy: {
          overrideMode,
          overrideSlots: overrideMode === 'selected' ? parsedSlots : [],
          localBreakDefault: inviteForm.localBreakDefault,
          logHorizonHours: Number(inviteForm.logHorizonHours),
          syncScopes: [
            'categories', 'assets', 'breaks',
            ...(inviteForm.advertisingEnabled && Number(status.rolloutStage) >= 2
              ? ['advertising']
              : []),
            'clocks', 'schedules',
            ...(Number(status.rolloutStage) >= 2 ? ['logs'] : []),
          ],
        },
      }, { withCredentials: true });
      setInvite(response.data);
      setMessage({ severity: 'success', text: 'One-time franchise invitation created.' });
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
    } finally {
      setWorking('');
    }
  };

  const acceptInvite = async () => {
    setWorking('accept');
    setMessage(null);
    try {
      await axios.post(`${API_URL}/api/syndication/accept`, acceptForm, { withCredentials: true });
      setAcceptForm({ inviteCode: '', destinationStationId: '' });
      setMessage({ severity: 'success', text: 'Parent station linked. Run the first sync when ready.' });
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
    } finally {
      setWorking('');
    }
  };

  const linkAction = async (link, action) => {
    const key = `${action}:${link.link_id}`;
    setWorking(key);
    setMessage(null);
    try {
      let response;
      if (action === 'sync') {
        response = await axios.post(`${API_URL}/api/syndication/links/${link.link_id}/sync`, {}, { withCredentials: true });
        setMessage({ severity: 'success', text: `Sync complete: ${response.data.bytesDownloaded || 0} bytes downloaded.` });
      } else if (action === 'reissue') {
        response = await axios.post(`${API_URL}/api/syndication/links/${link.link_id}/reissue`, {}, { withCredentials: true });
        setInvite(response.data);
        setMessage({ severity: 'success', text: 'A replacement one-time invitation was created.' });
      } else {
        await axios.post(`${API_URL}/api/syndication/links/${link.link_id}/status`, { status: action }, { withCredentials: true });
        setMessage({ severity: 'success', text: `Link ${action}.` });
      }
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
    } finally {
      setWorking('');
    }
  };

  const setAdvertisingScope = async (link, enabled) => {
    const key = `scopes:${link.link_id}`;
    setWorking(key);
    setMessage(null);
    try {
      const scopes = Array.isArray(link.sync_scopes) ? link.sync_scopes : [];
      const next = enabled
        ? [...new Set([...scopes, 'advertising'])]
        : scopes.filter((scope) => scope !== 'advertising');
      await axios.post(
        `${API_URL}/api/syndication/links/${link.link_id}/scopes`,
        { sync_scopes: next },
        { withCredentials: true }
      );
      setMessage({
        severity: 'success',
        text: `Mandatory franchise campaign distribution ${enabled ? 'enabled' : 'disabled'}.`,
      });
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
    } finally {
      setWorking('');
    }
  };

  const saveOverridePolicy = async (link) => {
    const draft = policyDrafts[link.link_id] || { mode: 'none', slots: '' };
    const requestedSlots = String(draft.slots || '').split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    const parsedSlots = parseSlots(draft.slots);
    if (draft.mode === 'selected'
        && (!requestedSlots.length || parsedSlots.length !== requestedSlots.length)) {
      setMessage({
        severity: 'error',
        text: 'Selected override hours must all use day:hour format (day 0-6, hour 0-23).',
      });
      return;
    }
    const key = `policy:${link.link_id}`;
    setWorking(key);
    setMessage(null);
    try {
      await axios.post(
        `${API_URL}/api/syndication/links/${link.link_id}/override-policy`,
        {
          override_mode: draft.mode,
          override_slots: draft.mode === 'selected' ? parsedSlots : [],
        },
        { withCredentials: true }
      );
      setMessage({ severity: 'success', text: 'Child clock override policy updated.' });
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: error.response?.data?.message || error.message });
    } finally {
      setWorking('');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}
      {status.warning && <Alert severity="warning" sx={{ mb: 2 }}>{status.warning}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LinkIcon color="primary" /> Create parent invitation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The code expires after 15 minutes and can be accepted once by another tenant superadmin.
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Parent station</InputLabel>
                  <Select value={inviteForm.sourceStationId} label="Parent station"
                    onChange={(event) => setInviteForm({ ...inviteForm, sourceStationId: event.target.value })}>
                    {stations.map((station) => <MenuItem key={station.id} value={station.id}>{station.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Child clock overrides</InputLabel>
                  <Select
                    value={Number(status.rolloutStage) >= 3 ? inviteForm.overrideMode : 'none'}
                    disabled={Number(status.rolloutStage) < 3}
                    label="Child clock overrides"
                    onChange={(event) => setInviteForm({ ...inviteForm, overrideMode: event.target.value })}>
                    <MenuItem value="none">Disabled</MenuItem>
                    <MenuItem value="selected">Selected hours only</MenuItem>
                    <MenuItem value="all">All hours</MenuItem>
                  </Select>
                </FormControl>
                {Number(status.rolloutStage) >= 3 && inviteForm.overrideMode === 'selected' && (
                  <TextField label="Allowed slots" value={inviteForm.overrideSlots}
                    onChange={(event) => setInviteForm({ ...inviteForm, overrideSlots: event.target.value })}
                    helperText="Comma-separated day:hour values. Sunday=0, e.g. 1:9,1:10,5:18" />
                )}
                <FormControl fullWidth>
                  <InputLabel>Local break default</InputLabel>
                  <Select value={inviteForm.localBreakDefault} label="Local break default"
                    onChange={(event) => setInviteForm({ ...inviteForm, localBreakDefault: event.target.value })}>
                    <MenuItem value="parent">Keep parent commercials</MenuItem>
                    <MenuItem value="local">Local placeholders, parent fallback</MenuItem>
                    <MenuItem value="filler">Local filler, parent fallback</MenuItem>
                  </Select>
                </FormControl>
                <TextField type="number" label="Cached log horizon (hours)" value={inviteForm.logHorizonHours}
                  inputProps={{ min: 24, max: 744 }}
                  onChange={(event) => setInviteForm({ ...inviteForm, logHorizonHours: event.target.value })} />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={inviteForm.advertisingEnabled && Number(status.rolloutStage) >= 2}
                      disabled={Number(status.rolloutStage) < 2}
                      onChange={(event) => setInviteForm({
                        ...inviteForm,
                        advertisingEnabled: event.target.checked,
                      })}
                    />
                  )}
                  label={Number(status.rolloutStage) >= 2
                    ? 'Allow mandatory franchise campaign distribution'
                    : 'Mandatory franchise campaigns require rollout stage 2'}
                />
                <Button variant="contained" disabled={!inviteForm.sourceStationId || working === 'invite'} onClick={createInvite}>
                  {working === 'invite' ? 'Generating…' : 'Generate invitation'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Join a parent station</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the one-time code from the parent and choose the local station that will receive its format.
              </Typography>
              <Stack spacing={2}>
                <TextField label="Invitation code" value={acceptForm.inviteCode}
                  onChange={(event) => setAcceptForm({ ...acceptForm, inviteCode: event.target.value.toUpperCase() })} />
                <FormControl fullWidth>
                  <InputLabel>Destination station</InputLabel>
                  <Select value={acceptForm.destinationStationId} label="Destination station"
                    onChange={(event) => setAcceptForm({ ...acceptForm, destinationStationId: event.target.value })}>
                    {stations.map((station) => <MenuItem key={station.id} value={station.id}>{station.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="contained" disabled={!acceptForm.inviteCode || !acceptForm.destinationStationId || working === 'accept'} onClick={acceptInvite}>
                  {working === 'accept' ? 'Linking…' : 'Accept and link'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {invite?.inviteCode && (
        <Alert severity="info" sx={{ mt: 2 }}
          action={<Button color="inherit" startIcon={<ContentCopy />} onClick={() => navigator.clipboard.writeText(invite.inviteCode)}>Copy</Button>}>
          Invitation: <strong>{invite.inviteCode}</strong> · expires {new Date(invite.expiresAt).toLocaleString()}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Linked stations</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>
      {[...parentLinks, ...childLinks].length === 0 ? (
        <Alert severity="info">No franchise links are configured.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {[...parentLinks, ...childLinks].map((link) => (
            <Card key={link.link_id} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="subtitle1">
                      {link.role === 'parent' ? 'Parent publication' : 'Franchise child'} · station {link.role === 'parent' ? link.source_station_id : link.destination_station_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {link.parent_tenant_id} → {link.child_tenant_id || 'awaiting acceptance'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Overrides: {link.override_mode}{link.override_mode === 'selected' ? ` (${slotSummary(link.override_slots)})` : ''}
                      {' · '}Mapped: {link.mapped_entities || 0} · Cached hours: {link.cached_hours || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Last success: {link.last_success_at ? new Date(link.last_success_at).toLocaleString() : 'never'}
                      {' · '}Cached through: {link.cached_through ? new Date(link.cached_through).toLocaleString() : 'not available'}
                      {' · '}Stale/missing hours: {link.stale_hours || 0}
                      {' · '}Missing audio: {link.last_entity_counts?.assets_missing || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Next sync: {link.next_sync_at ? new Date(link.next_sync_at).toLocaleString() : 'not scheduled'}
                      {' · '}Revision: {link.last_sync_revision ? String(link.last_sync_revision).slice(0, 12) : 'none'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Scopes: {(link.sync_scopes || []).join(', ') || 'none'}
                    </Typography>
                  </Box>
                  <Chip label={link.status} color={link.status === 'active' ? 'success' : 'default'} />
                </Box>
                {link.last_error && <Alert severity="error" sx={{ mt: 1 }}>{link.last_error}</Alert>}
                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                  {link.role === 'parent' && link.status !== 'revoked' && Number(status.rolloutStage) >= 2 && (
                    <FormControlLabel
                      control={(
                        <Switch
                          size="small"
                          checked={(link.sync_scopes || []).includes('advertising')}
                          disabled={working === `scopes:${link.link_id}`}
                          onChange={(event) => setAdvertisingScope(link, event.target.checked)}
                        />
                      )}
                      label="Mandatory campaigns"
                    />
                  )}
                  {link.role === 'parent' && link.status !== 'revoked' && Number(status.rolloutStage) >= 3 && (
                    <>
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel>Child clock overrides</InputLabel>
                        <Select
                          value={policyDrafts[link.link_id]?.mode || 'none'}
                          label="Child clock overrides"
                          onChange={(event) => setPolicyDrafts((current) => ({
                            ...current,
                            [link.link_id]: {
                              ...(current[link.link_id] || {}),
                              mode: event.target.value,
                            },
                          }))}
                        >
                          <MenuItem value="none">Disabled</MenuItem>
                          <MenuItem value="selected">Selected hours</MenuItem>
                          <MenuItem value="all">All hours</MenuItem>
                        </Select>
                      </FormControl>
                      {policyDrafts[link.link_id]?.mode === 'selected' && (
                        <TextField
                          size="small"
                          label="Allowed day:hour slots"
                          value={policyDrafts[link.link_id]?.slots || ''}
                          onChange={(event) => setPolicyDrafts((current) => ({
                            ...current,
                            [link.link_id]: {
                              ...(current[link.link_id] || {}),
                              slots: event.target.value,
                            },
                          }))}
                        />
                      )}
                      <Button
                        size="small"
                        disabled={working === `policy:${link.link_id}`}
                        onClick={() => saveOverridePolicy(link)}
                      >
                        Save override policy
                      </Button>
                    </>
                  )}
                  {link.role === 'child' && link.status === 'active' && (
                    <Button size="small" variant="contained" startIcon={<SyncIcon />}
                      disabled={working === `sync:${link.link_id}`} onClick={() => linkAction(link, 'sync')}>
                      Sync now
                    </Button>
                  )}
                  {link.role === 'parent' && link.status === 'pending' && (
                    <Button size="small" onClick={() => linkAction(link, 'reissue')}>Reissue invite</Button>
                  )}
                  {link.status === 'active' && (
                    <Button size="small" color="warning" onClick={() => linkAction(link, 'suspended')}>Suspend</Button>
                  )}
                  {link.status !== 'revoked' && (
                    <Button size="small" color="error" onClick={() => linkAction(link, 'revoked')}>Detach</Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
